import { macroCondition, isTesting } from '@embroider/macros';
import { getOwner } from '@ember/application';
import { registerDestructor } from '@ember/destroyable';
import { task, timeout, race, waitForEvent } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import bucketItems from './bucket-items';
import Feed from './feed';
import normalizeIdentifier from 'ember-stereo/-private/utils/normalize-identifier';
import { action } from '@ember/object';
import { next } from '@ember/runloop';

function setItemState(item, state) {
  for (let key of Object.keys(state)) {
    if (item[key] !== state[key]) {
      item[key] = state[key];
    }
  }
}

export default class PlaybackFeed extends Feed {
  kind = 'playback'

  @tracked episodeIndex;
  @tracked resumePositionMs = null;

  _lastResumeSaveAt = 0;

  constructor(airing, options = {}) {
    super(airing, options);
    // This can either be an airing, or a now-playing object
    this.feedObject = airing;
    this.station = airing.station;
    this.broadcast = airing.broadcast;
    this.airing = airing
    this.episodeIndex = options.episodeIndex ?? 0;

    this.resumePositionMs = this.resumeStore?.positionFor(airing) ?? null;

    this.startTask.perform()
    registerDestructor(this, this.teardown.bind(this));
  }

  // Overriding an override keeps the airing and changes its show, so the show is never copied.
  get show() {
    return this.airing?.show;
  }

  // Optional seam: a host app may register a `playback-resume` service that
  // remembers where a listener left off inside an airing. The embeddable backs
  // it with localStorage, and an authenticated host could back it with the API.
  // When no such service is registered, resume is simply inert.
  get resumeStore() {
    try {
      return getOwner(this)?.lookup('service:playback-resume') ?? null;
    } catch (e) {
      return null;
    }
  }

  // A click handler performing the task passes its event here, which is not a start time.
  resolveStartTime(trackStartTime) {
    let isPosition = typeof trackStartTime === 'number' || trackStartTime instanceof Date;
    if (isPosition && trackStartTime) return trackStartTime;
    return this.resumePositionMs ?? 0;
  }

  // Where a freshly-cast archive begins on the device. Only a concrete ms offset
  // is castable via LoadRequest.currentTime. A Date-based wall-clock seek falls
  // back to the post-load seek path.
  castStartTime(trackStartTime) {
    let start = this.resolveStartTime(trackStartTime);
    return typeof start === 'number' && start > 0 ? start : null;
  }

  // Seconds, for the connection's own start position. Only a concrete ms offset
  // translates. A Date-based wall-clock seek still goes through the post-load
  // seek path in setSoundPositionTask.
  loadStartPosition(trackStartTime) {
    let start = this.castStartTime(trackStartTime);
    return start == null ? null : start / 1000;
  }

  // The airing's window, matching both the player's scrubber and the live feed
  // this was rewound from, so crossing between them doesn't rescale the bar.
  get timeline() {
    let airing = this.airing;
    if (!airing?.startsAt || !airing?.endsAt) return null;

    let duration = airing.endsAt.getTime() - airing.startsAt.getTime();
    if (duration <= 0) return null;

    let feed = this;
    // A live position lets the metadata carrying this outlive the tick it was built on.
    return {
      duration,
      get position() {
        return Math.min(Math.max(feed.playheadPositionMs, 0), duration);
      },
    };
  }

  get mediaSessionActions() {
    // Through the feed so a lock-screen seek gets the same held playhead as an
    // in-app one, rather than the connection's mid-seek report.
    return { seekto: (positionMs) => this.seekOrGoLive(positionMs) };
  }


  // As Feed, but an archive with nothing loaded yet still knows where it would
  // resume, which is what the scrubber should show before playback starts.
  get playheadPositionMs() {
    if (this.pendingPositionMs != null) {
      return this.pendingPositionMs;
    }
    if (this.loadedSound?.isResolved && Number.isFinite(this.transportPosition)) {
      return this.transportPosition;
    }
    return this.resumePositionMs ?? 0;
  }

  persistResumePosition() {
    if (!this.loadedSound?.isResolved || !this.resumeStore) return;
    this._lastResumeSaveAt = Date.now();
    // transportPosition follows the outlet while casting, the local sound
    // otherwise — so a resume point saved mid-cast reflects the device.
    this.resumeStore.save(this.airing, this.transportPosition);
  }

  // Throttled save used by the periodic playback loop, so a position-changed
  // firehose doesn't hammer localStorage. Terminal events (pause/end) save
  // directly via persistResumePosition / markListened.
  throttledPersistResumePosition() {
    if (Date.now() - this._lastResumeSaveAt < 5000) return;
    this.persistResumePosition();
  }

  // Played to the end: mark it fully listened (full progress bar, no longer
  // "continue listening") rather than resuming at the very end next time.
  markListened() {
    this.resumeStore?.markComplete(this.airing);
    this.resumePositionMs = null;
  }

  get isLive() {
    return this.clock.date >= this.airing.startsAt && this.clock.date <= this.airing.endsAt
  }

  get imageUrl() {
    return this.imageSource?.medium_square?.url
  }

  get url() {
    return this.airing?.authenticatedPlaybackUrl || this.airing?.playbackUrl
  }

  get playbackState() {
    return this.airing?.playbackState
  }

  get pastPlaybackLimit() {
    return this.playbackState == 'expired'
  }

  get canPlay() {
    return !!this.url && !this.pastPlaybackLimit;
  }

  // Playable remotely whenever it's playable at all. The actual remote URL is a
  // token-signed link fetched on demand (fetchRemotePlaybackUrl) rather than
  // advertised on every airing payload.
  get canRemotePlay() {
    return this.canPlay;
  }

  // The signed URL is an extra_attribute the API omits by default, so it's only
  // available once explicitly fetched. Cached here once resolved.
  @tracked _remotePlaybackUrl = null;

  get remotePlaybackUrl() {
    return this._remotePlaybackUrl ?? this.airing?.remotePlaybackUrl ?? null;
  }

  // Fetch the token-signed URL on demand. Goes straight through the airing
  // adapter (its host + auth headers) and requests only the extra_attribute, so
  // the bearer token never rides along on a normal airing payload.
  async fetchRemotePlaybackUrl() {
    let cached = this.remotePlaybackUrl;
    if (cached) return cached;

    let id = this.airing?.id;
    if (!id) return null;

    let adapter = this.store.adapterFor('airing');
    let base = adapter.buildURL('airing', id);
    let separator = base.includes('?') ? '&' : '?';
    let url = `${base}${separator}extra_fields[airings]=remote_playback_url`;

    try {
      let response = await fetch(url, {
        // The airing resource is HTTP-cached (cache_resource), so a prior plain
        // airing load can otherwise 304 this request and hand back a body that
        // never had the on-demand attribute.
        cache: 'no-store',
        headers: {
          Accept: 'application/vnd.api+json',
          ...(adapter.headers ?? {}),
        },
      });
      if (!response.ok) return null;

      let json = await response.json();
      let attributes = json?.data?.attributes ?? {};
      this._remotePlaybackUrl = attributes.remote_playback_url ?? null;
      return this._remotePlaybackUrl;
    } catch (e) {
      return null;
    }
  }

  // Before any audio loads a seek only decides where play will start from.
  seek(positionMs) {
    if (this.loadedSound?.isResolved) {
      super.seek(positionMs);
      return;
    }
    this.resumePositionMs = positionMs;
    this.pendingPositionMs = null;
    this.updateNowPlayingAttributes.perform();
  }

  preview(positionMs) {
    this.pendingPositionMs = positionMs;
    this.updateNowPlayingAttributes.perform();
  }

  // An in-progress manifest trails wall time by a segment plus its cache window, so audio stalls short of elapsedMs.
  static LIVE_EDGE_LEAD_MS = 30000;

  // Only a sped-up archive can reach the edge on its own.
  goLiveIfCaughtUp() {
    if (!(this.stereo.playbackSpeed > 1) || !this.transportIsPlaying || !this.isInProgress) return;
    if (this.transportPosition >= this.elapsedMs - PlaybackFeed.LIVE_EDGE_LEAD_MS) {
      this.feeds.goLive();
    }
  }

  startTask = task(async () => {
    next(() => this.isLoaded = true)
    while(true) {
      if (this.loadedSound) {
        // The Sound relays its backing connection's position events — including
        // the AirPlay outlet's while casting — so this waits the same way either
        // way (the 500ms tick is just a floor).
        await race([
          waitForEvent(this.loadedSound, 'audio-played'),
          waitForEvent(this.loadedSound, 'audio-position-changed'),
          waitForEvent(this.loadedSound, 'audio-position-will-change')
        ]);
        this.updateNowPlayingAttributes.perform();
        this.throttledPersistResumePosition();
        this.playback.reportProgress(this);
        this.goLiveIfCaughtUp();
      } else if (macroCondition(isTesting())) {
        return;
      }
      await timeout(500);
    }
  });

  loadTask = task({ restartable: true, maxConcurrency: 2 }, async (trackStartTime = 0) => {
    this.pendingPositionMs = this.castStartTime(trackStartTime);

    // Refresh the access token if stale before signing/loading the URL, so an
    // idle tab doesn't load with an expired token and 403. Inert when the host's
    // settings service doesn't provide it. Warm the HLS chunk alongside it — the
    // token refresh is a network round trip we'd otherwise spend doing nothing.
    await Promise.all([
      this.settings?.ensureFreshTokenTask?.perform?.(),
      this.stereo.prewarmConnection?.('HLS'),
    ]);

    // Load if unresolved — or while casting, so returning to this feed
    // re-resolves it onto the device (its prior cast connection is stale once the
    // session moved on). loadTask no-ops if it's already the active cast.
    let args = await this.castAwareLoadArgs(trackStartTime, {
      useConnections: ['HLS', 'NativeAudio'],
      metadata: { airing: this.airing },
      xhr: { manifestOnly: true, headers: this.session?.authenticatedHeaders },
    });
    if (args) {
      try {
        await this.stereo.loadTask.linked().perform(...args);
      } catch (e) {
        throw new Error('failed to load sound', { cause: e });
      }
    }

    await this.setSoundPositionTask.perform(this.resolveStartTime(trackStartTime));
    await this.updateNowPlayingAttributes.perform();

    if (this.stereo.currentSound) {
      this.stereo.currentSound = this.loadedSound;
    }

    await timeout(200);
  });

  playTask = task({ restartable: true, maxConcurrency: 2 }, async (trackStartTime = 0) => {
    this.pendingPositionMs = this.castStartTime(trackStartTime);

    // Refresh the access token if stale before signing/loading the URL, so an
    // idle tab doesn't load with an expired token and 403. Inert when the host's
    // settings service doesn't provide it. Warm the HLS chunk alongside it rather
    // than after it — the token refresh is a network round trip we'd otherwise
    // spend doing nothing.
    await Promise.all([
      this.settings?.ensureFreshTokenTask?.perform?.(),
      this.stereo.prewarmConnection?.('HLS'),
    ]);

    let args = await this.castAwareLoadArgs(trackStartTime, {
      useConnections: ['HLS', 'NativeAudio'],
      metadata: { airing: this.airing },
      xhr: { manifestOnly: true, headers: this.session?.authenticatedHeaders },
    });
    if (args) {
      try {
        await this.stereo.playTask.linked().perform(...args);
      } catch (e) {
        throw new Error('failed to load sound', { cause: e });
      }
    }

    await this.setSoundPositionTask.perform(this.resolveStartTime(trackStartTime));

    if (this.loadedSound?.isResolved && !this.loadedSound.isPlaying) {
      await this.loadedSound.togglePause();
    }

    this.pendingPositionMs = null;

    // yield sound.play();
    this.updateNowPlayingAttributes.perform();
    await timeout(200);
  });

  setSoundPositionTask = task({ restartable: true }, async trackStartTime => {
    let sound = this.loadedSound;

    if (!sound) { return }

    if (
      trackStartTime &&
      typeof trackStartTime.getTime === 'function' &&
      trackStartTime.getTime()
    ) {
      var position;
      if (sound.currentTime) {
        position =
          sound.position +
          trackStartTime.getTime() -
          sound.currentTime.getTime();
      } else {
        position = trackStartTime.getTime() - this.airing.startsAt.getTime();
      }
      sound.position = position;
      await timeout(200);
    } else if (trackStartTime && typeof trackStartTime === 'number') {
      // The connection may already have loaded AT this offset (loadStartPosition),
      // in which case seeking there again just flushes the buffer it filled and
      // refetches the same fragments.
      let drift = Math.abs((sound.position ?? 0) - trackStartTime);
      if (drift < Feed.SEEK_TOLERANCE_MS) {
        return;
      }

      sound.position = trackStartTime;
      await timeout(200);
    }
  });

  // keepLatest with a trailing cooldown throttles a drag to one rebucket per 200ms instead of waiting for it to stop.
  updateNowPlayingAttributes = task({ keepLatest: true }, async () => {
    // The Sound's position/duration follow the AirPlay outlet while casting, so
    // bucketing off it advances the now-playing track with what the device is
    // actually playing — no separate remote branch needed. Before it loads, or
    // while a drag holds a target, the playhead stands in.
    let bucketSound = this.loadedSound;
    if (!bucketSound?.isResolved || this.pendingPositionMs != null) {
      bucketSound = { position: this.playheadPositionMs, duration: bucketSound?.duration };
    }

    let {
      played = [],
      unplayed = [],
      playing,
    } = bucketItems(this.items, this.airing, bucketSound);

    // Tracked writes dirty even when equal, and each one rerenders a playlist row.
    played.forEach((t) => setItemState(t, { hasPlayed: true, isPlaying: false }));
    unplayed.forEach((t) => setItemState(t, { hasPlayed: false, isPlaying: false }));
    if (playing) {
      setItemState(playing, { hasPlayed: false, isPlaying: true });
    }

    if (this._nowPlayingSynced && playing === this.item) {
      await timeout(200);
      return;
    }
    this._nowPlayingSynced = true;

    let itemType = playing?.type;
    let item = playing;

    // Exactly one is ever set: a finished track left here gets picked back up
    // by updateAudioMetadata's most-recent-item sort.
    this.track = itemType === 'track' ? item : undefined;
    this.traffic = itemType === 'traffic' ? item : undefined;
    this.note = itemType === 'note' ? item : undefined;

    this.item = item;
    this.itemType = itemType;

    try {
      this.feeds.updateAudioMetadata.perform(this.identifier, {
        station: this.station,
        broadcast: this.broadcast,
        sound: this.loadedSound,
        airing: this.airing,
        track: this.track,
        note: this.note,
        traffic: this.traffic,
        imageSource: this.imageSource,
        timeline: this.timeline,
        item: item,
        itemType
      });
    } catch(e) {
      console.error(`error updating audio metadata`, e)
    }

    await timeout(200);
  });

  teardown() {
  }
}
